import { useEffect, useState } from "react";
import API_BASE from "../../../../config";
import { authHeaders, empName } from "../sectionUtils";
import s from "./SectionOverviewTab.module.css";
import { syLabel } from "../../../../utils/schoolYear";

export default function SectionOverviewTab({ section, gradeLevels = [], schoolYears = [] }) {
  const [room,      setRoom]      = useState(null);
  const [adviser,   setAdviser]   = useState(null);
  const [loading,   setLoading]   = useState(false);

  const gradeLevelById = Object.fromEntries(gradeLevels.map(g => [g.id, g]));
  const syById         = Object.fromEntries(schoolYears.map(y => [y.id, y]));
  const gradeLevel     = section?.gradeLevelId ? gradeLevelById[section.gradeLevelId] : null;
  const sy             = section?.schoolYearId ? syById[section.schoolYearId]         : null;

  useEffect(() => {
    if (!section) return;
    let cancelled = false;
    setLoading(true);

    const fetches = [];
    if (section.homeroomId) {
      fetches.push(
        fetch(`${API_BASE}/api/rooms/${section.homeroomId}`, { headers: authHeaders() })
          .then(r => r.ok ? r.json() : null).catch(() => null)
      );
    } else {
      fetches.push(Promise.resolve(null));
    }
    if (section.adviserStaffId) {
      fetches.push(
        fetch(`${API_BASE}/api/employees/${section.adviserStaffId}`, { headers: authHeaders() })
          .then(r => r.ok ? r.json() : null).catch(() => null)
      );
    } else {
      fetches.push(Promise.resolve(null));
    }

    Promise.all(fetches).then(([roomData, adviserData]) => {
      if (cancelled) return;
      setRoom(roomData);
      setAdviser(adviserData);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [section?.id]);

  if (!section) return null;

  return (
    <div className={s.root}>
      {/* Section identity card */}
      <div className={s.identityCard}>
        <div className={s.identityIcon} aria-hidden="true">🏫</div>
        <div className={s.identityInfo}>
          <div className={s.sectionName}>{section.name}</div>
          <div className={s.identityMeta}>
            {gradeLevel && <span className={s.chip}>{gradeLevel.name}</span>}
            {sy         && <span className={s.chip}>SY {sy.label}</span>}
          </div>
        </div>
      </div>

      <div className={s.grid}>
        {/* Room card */}
        <div className={s.infoCard}>
          <div className={s.cardHead}>
            <span className={s.cardIcon} aria-hidden="true">📍</span>
            <span className={s.cardTitle}>Homeroom</span>
          </div>
          {loading ? (
            <div className={s.loadingText}>Loading…</div>
          ) : room ? (
            <dl className={s.dl}>
              <div className={s.dlRow}>
                <dt>Room</dt>
                <dd>{room.name}</dd>
              </div>
              {room.building && (
                <div className={s.dlRow}>
                  <dt>Building</dt>
                  <dd>{room.building}</dd>
                </div>
              )}
              {room.floor != null && (
                <div className={s.dlRow}>
                  <dt>Floor</dt>
                  <dd>{room.floor}</dd>
                </div>
              )}
              {room.roomType && (
                <div className={s.dlRow}>
                  <dt>Type</dt>
                  <dd>{room.roomType}</dd>
                </div>
              )}
              {room.capacity && (
                <div className={s.dlRow}>
                  <dt>Capacity</dt>
                  <dd>{room.capacity} seats</dd>
                </div>
              )}
            </dl>
          ) : (
            <div className={s.emptyText}>No homeroom assigned</div>
          )}
        </div>

        {/* Adviser card */}
        <div className={s.infoCard}>
          <div className={s.cardHead}>
            <span className={s.cardIcon} aria-hidden="true">👤</span>
            <span className={s.cardTitle}>Class Adviser</span>
          </div>
          {loading ? (
            <div className={s.loadingText}>Loading…</div>
          ) : adviser ? (
            <div className={s.adviserBlock}>
              <div className={s.adviserName}>{empName(adviser)}</div>
              {adviser.email && <div className={s.adviserSub}>{adviser.email}</div>}
              {adviser.contact && <div className={s.adviserSub}>📞 {adviser.contact}</div>}
            </div>
          ) : (
            <div className={s.emptyText}>No adviser assigned</div>
          )}
        </div>

        {/* Grade & year */}
        <div className={s.infoCard}>
          <div className={s.cardHead}>
            <span className={s.cardIcon} aria-hidden="true">🎓</span>
            <span className={s.cardTitle}>Grade &amp; Year</span>
          </div>
          <dl className={s.dl}>
            <div className={s.dlRow}>
              <dt>Grade Level</dt>
              <dd>{gradeLevel?.name || "—"}</dd>
            </div>
            <div className={s.dlRow}>
              <dt>School Year</dt>
              <dd>{sy ? syLabel(sy.label) : "—"}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
